import { MapPin, Phone, Clock, ShoppingBag } from "lucide-react";
import { STORE, UBER_EATS_URL } from "@/config/site";

export default function Location() {
  return (
    <section
      id="location"
      data-testid="location-section"
      className="relative bg-[#0A0A0A] py-16 sm:py-24 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <span className="inline-block font-subheading font-bold uppercase tracking-[0.3em] text-brand-orange text-xs mb-4">
            Find us
          </span>
          <h2 className="font-heading text-5xl sm:text-7xl text-white uppercase tracking-tight leading-none">
            Visit. Collect. <br />
            <span className="text-brand-green">Take-away only.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Info card */}
          <div
            data-testid="location-info-card"
            className="bg-brand-surface rounded-3xl p-8 sm:p-10 border border-white/10 flex flex-col gap-7"
          >
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-brand-orange/15 text-brand-orange flex items-center justify-center">
                <MapPin size={22} />
              </div>
              <div>
                <h4 className="font-subheading font-bold uppercase tracking-widest text-white text-sm mb-1">
                  Address
                </h4>
                <p className="font-body text-white/80 leading-relaxed">
                  {STORE.address_line1}<br />{STORE.address_line2}
                </p>
                <a
                  href={STORE.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="location-maps-link"
                  className="inline-block mt-2 font-subheading font-semibold uppercase tracking-widest text-xs text-brand-orange hover:underline"
                >
                  Get Directions →
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-brand-green/15 text-brand-green flex items-center justify-center">
                <Phone size={22} />
              </div>
              <div>
                <h4 className="font-subheading font-bold uppercase tracking-widest text-white text-sm mb-1">
                  Phone
                </h4>
                <a
                  href={STORE.phone_tel}
                  data-testid="location-phone-link"
                  className="font-body text-white/80 hover:text-brand-orange transition-colors"
                >
                  {STORE.phone}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-brand-teal/15 text-brand-teal flex items-center justify-center">
                <ShoppingBag size={22} />
              </div>
              <div>
                <h4 className="font-subheading font-bold uppercase tracking-widest text-white text-sm mb-1">
                  Service
                </h4>
                <p className="font-body text-white/80">
                  {STORE.service} — no dine-in. Order on Uber Eats or call to collect.
                </p>
              </div>
            </div>

            <a
              href={UBER_EATS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-order-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-[#FF7A1A] text-white font-subheading font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full transition-all shadow-[0_8px_30px_0_rgba(255,102,0,0.4)] hover:-translate-y-0.5"
            >
              Order on Uber Eats
            </a>
          </div>

          {/* Hours card */}
          <div
            data-testid="location-hours-card"
            className="bg-brand-surface rounded-3xl p-8 sm:p-10 border border-white/10"
          >
            <div className="flex items-center gap-4 mb-7">
              <div className="shrink-0 w-12 h-12 rounded-full bg-brand-orange/15 text-brand-orange flex items-center justify-center">
                <Clock size={22} />
              </div>
              <h4 className="font-heading text-3xl sm:text-4xl text-white uppercase leading-none">
                Opening Hours
              </h4>
            </div>

            <ul className="divide-y divide-white/5">
              {STORE.hours_detail.map((row) => (
                <li
                  key={row.day}
                  data-testid={`hours-row-${row.day.toLowerCase()}`}
                  className="flex items-center justify-between py-3.5"
                >
                  <span className="font-subheading font-semibold uppercase tracking-widest text-white/85 text-sm">
                    {row.day}
                  </span>
                  <span className="font-body text-white/70 text-sm">{row.time}</span>
                </li>
              ))}
            </ul>

            <p className="mt-7 font-body text-white/55 text-xs leading-relaxed">
              Hours apply across all 7 days. Final orders accepted 15 minutes before closing.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
